/* __next_internal_client_entry_do_not_use__ Default auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useSitecoreContext } from 'lib/hooks/useSitecoreContext';
import { Suspense, useMemo } from 'react';
import { tv } from 'tailwind-variants';
import { withStandardComponentWrapper } from 'helpers/HOC';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { getTestProps } from 'lib/testing/utils';
import { parseStyleParams } from 'lib/utils/style-param-utils';
import { getCtaStyle } from 'lib/utils/cta-utils';
import { createCTAClickGTMEvent } from 'lib/utils/gtm-utils';
import useDictionary from 'lib/hooks/useDictionary';
import { findComponent } from 'lib/utils/object-utils';
import dynamic from 'next/dynamic';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
import useIsEditing from 'lib/hooks/useIsEditing';
const LocationCard = (props)=>{
    const isEditing = useIsEditing();
    // Extract data from component fields and route fields
    const {} = props?.fields || {};
    const { sitecoreContext } = useSitecoreContext();
    const currentPage = sitecoreContext?.route;
    const routeItemId = sitecoreContext?.route?.itemId ?? '';
    const uniqueMapId = ('map-' + routeItemId).replace(/-/g, '');
    const GoogleMapRender = dynamic(()=>import('helpers/GoogleMapRender/GoogleMapRender'), {
        loadableGenerated: {
            webpack: ()=>[
                    require.resolveWeak("helpers/GoogleMapRender/GoogleMapRender")
                ]
        },
        ssr: false,
        loading: ()=>/*#__PURE__*/ _jsx("p", {
                children: "Map is Loading..."
            })
    });
    // Route fields for location data
    const { locationName, telephone, fax, openingHours, primaryCTA, secondaryCTA, latitude, longitude, addressFirstLine, addressSecondLine, addressThirdLine, city, postCode, countryStateProvince, country, dxNumber } = currentPage?.fields ?? {};
    const { getDictionaryValue } = useDictionary();
    // Parse style parameters for component styling
    const styles = parseStyleParams(props.params, [
        'cta1',
        'cta2'
    ]);
    // Google Maps data
    const locationTitle = locationName?.value || 'Office Location';
    const mapData = useMemo(()=>({
            latitude: latitude?.value,
            longitude: longitude?.value,
            mapId: uniqueMapId,
            locationTitle: locationTitle
        }), [
        latitude?.value,
        longitude?.value,
        uniqueMapId,
        locationTitle
    ]);
    // Create Google Maps directions URL
    const createDirectionsUrl = ()=>{
        if (!latitude?.value || !longitude?.value) return '';
        const fullAddress = [
            addressFirstLine?.value,
            addressSecondLine?.value,
            addressThirdLine?.value,
            city?.value,
            postCode?.value,
            countryStateProvince?.value,
            country?.value
        ].filter(Boolean).join(' ');
        return `https://www.google.com/maps?q=${latitude.value},${longitude.value}&daddr=${encodeURIComponent(fullAddress)}&ll=${latitude.value},${longitude.value}`;
    };
    // Address formatting with linked postcode
    const getFormattedAddress = ()=>{
        const firstLines = [
            addressFirstLine?.value
        ].filter(Boolean);
        const restOfAddress = [
            addressSecondLine?.value,
            addressThirdLine?.value,
            city?.value,
            postCode?.value ? `<a href="${createDirectionsUrl()}" target="_blank" rel="noopener noreferrer" class="text-color-text-link underline hover:text-color-text-link-hover">${postCode.value}</a>` : postCode?.value,
            countryStateProvince?.value,
            country?.value
        ].filter(Boolean);
        // Join first three lines with commas, then add break, then join rest with commas
        const addressParts = firstLines.join(', ') + (firstLines.length > 0 && restOfAddress.length > 0 ? '<br>' : '') + restOfAddress.join(', ');
        const dxSuffix = dxNumber?.value ? ` ${dxNumber.value}` : '';
        return {
            value: addressParts + dxSuffix
        };
    };
    // Contact field formatters
    const getTelephoneLink = ()=>{
        if (!telephone?.value) return undefined;
        const cleanPhone = telephone.value.replace(/\s/g, '');
        return `tel:${cleanPhone}`;
    };
    const getFaxLink = ()=>{
        if (!fax?.value) return undefined;
        const cleanFax = fax.value.replace(/\s/g, '');
        return `tel:${cleanFax}`;
    };
    const params = props?.params;
    const RenderingIdentifier = params?.['RenderingIdentifier'];
    const sectionItems = findComponent(sitecoreContext ?? {}, 'Section');
    const sectionTitle = sectionItems?.[0]?.fields?.title?.value ?? '';
    const { base, cardContainer, mapSection, mapImageStyle, detailsSection, detailsContent, detailsTitle, detailsTitleText, detailsTitleDivider, officeNameText, addressText, contactInfo, contactRow, contactLabel, telephoneLink, faxLink, openingHoursSection, openingHoursTitle, openingHoursText, ctaContainer, ctaButtons } = TAILWIND_VARIANTS();
    return /*#__PURE__*/ _jsx("div", {
        "data-component": "authorable/shared/content/location-card",
        ...editingHydrationProps(isEditing),
        id: RenderingIdentifier,
        ...getTestProps(`component-location-card-${props?.rendering?.uid}`),
        className: base(),
        children: /*#__PURE__*/ _jsxs("div", {
            className: cardContainer(),
            children: [
                mapData.latitude && mapData.longitude && mapData.mapId && /*#__PURE__*/ _jsx("div", {
                    className: mapSection(),
                    children: /*#__PURE__*/ _jsx(Suspense, {
                        fallback: /*#__PURE__*/ _jsx("p", {
                            children: "Map is Loading..."
                        }),
                        children: /*#__PURE__*/ _jsx(GoogleMapRender, {
                            latitude: mapData.latitude,
                            longitude: mapData.longitude,
                            mapId: mapData.mapId,
                            locationTitle: mapData.locationTitle,
                            className: mapImageStyle()
                        })
                    })
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: detailsSection(),
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: detailsContent(),
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: detailsTitle(),
                                children: [
                                    /*#__PURE__*/ _jsx("h3", {
                                        className: detailsTitleText(),
                                        ...getTestProps(`office-details-title`),
                                        children: getDictionaryValue('OfficeDetails')
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: detailsTitleDivider()
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(PlainTextWrapper, {
                                className: officeNameText(),
                                field: locationName,
                                tag: "p",
                                ...getTestProps(`office-name`)
                            }),
                            /*#__PURE__*/ _jsx(RichTextWrapper, {
                                className: addressText(),
                                field: getFormattedAddress(),
                                tag: "div",
                                ...getTestProps(`office-address`)
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: contactInfo(),
                                children: [
                                    telephone?.value && /*#__PURE__*/ _jsxs("div", {
                                        className: contactRow(),
                                        children: [
                                            /*#__PURE__*/ _jsx("a", {
                                                href: getTelephoneLink(),
                                                className: telephoneLink(),
                                                ...getTestProps(`telephone-link`),
                                                children: telephone.value
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: contactLabel(),
                                                children: "(Telephone)"
                                            })
                                        ]
                                    }),
                                    fax?.value && /*#__PURE__*/ _jsxs("div", {
                                        className: contactRow(),
                                        children: [
                                            /*#__PURE__*/ _jsx("a", {
                                                href: getFaxLink(),
                                                className: faxLink(),
                                                ...getTestProps(`fax-link`),
                                                children: fax.value
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: contactLabel(),
                                                children: "(FAX)"
                                            })
                                        ]
                                    })
                                ]
                            }),
                            openingHours?.value && /*#__PURE__*/ _jsxs("div", {
                                className: openingHoursSection(),
                                children: [
                                    /*#__PURE__*/ _jsx("h4", {
                                        className: openingHoursTitle(),
                                        ...getTestProps(`opening-hours-title`),
                                        children: getDictionaryValue('OpeningHours')
                                    }),
                                    /*#__PURE__*/ _jsx(RichTextWrapper, {
                                        className: openingHoursText(),
                                        field: openingHours,
                                        tag: "div",
                                        ...getTestProps(`opening-hours`)
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: ctaContainer(),
                                children: [
                                    /*#__PURE__*/ _jsx(LinkWrapper, {
                                        ctaComponentClass: "component-Location-block-button-1-color",
                                        className: ctaButtons(),
                                        ctaStyle: getCtaStyle(styles.cta1, 'primary'),
                                        field: primaryCTA,
                                        ctaVariant: "primary",
                                        ctaIcon: "arrow-right",
                                        suppressNewTabIcon: true,
                                        gtmEvent: createCTAClickGTMEvent({
                                            header: sectionTitle,
                                            ctaText: primaryCTA?.value?.text,
                                            ctaType: 'blue',
                                            componentName: 'Office Details',
                                            ctaLink: primaryCTA?.value?.href
                                        }),
                                        ...getTestProps(`contact-us-button`)
                                    }),
                                    /*#__PURE__*/ _jsx(LinkWrapper, {
                                        ctaComponentClass: "component-Location-block-button-2-color",
                                        className: ctaButtons(),
                                        ctaStyle: getCtaStyle(styles.cta2, 'secondary'),
                                        field: secondaryCTA,
                                        ctaVariant: "secondary",
                                        ctaIcon: "arrow-right",
                                        gtmEvent: createCTAClickGTMEvent({
                                            header: sectionTitle,
                                            ctaText: secondaryCTA?.value?.text,
                                            ctaType: 'clear',
                                            componentName: 'Office Details',
                                            ctaLink: secondaryCTA?.value?.href
                                        }),
                                        ...getTestProps(`secondary-cta`)
                                    })
                                ]
                            })
                        ]
                    })
                })
            ]
        })
    });
};
export const Default = withStandardComponentWrapper(LocationCard, false);
const TAILWIND_VARIANTS = tv({
    slots: {
        base: [
            'relative',
            'w-full'
        ],
        cardContainer: [
            'bg-component-card-item-surface',
            'rounded-general-border-radius-card',
            'shadow-[0px_0px_8px_0px_rgba(0,0,0,0.1)]',
            'overflow-hidden',
            'flex',
            'flex-col',
            'lg:flex-row',
            'w-full',
            'h-auto',
            'lg:h-[640px]'
        ],
        mapSection: [
            'w-full',
            'h-64',
            'lg:flex-1',
            'lg:h-full',
            'min-h-[320px]',
            'lg:min-h-full'
        ],
        mapImageStyle: [
            'w-full',
            'h-full',
            'border-0',
            'rounded-l-general-border-radius-card',
            'lg:rounded-bl-general-border-radius-card',
            'lg:rounded-tl-general-border-radius-card',
            'lg:rounded-tr-0',
            'lg:rounded-br-0'
        ],
        detailsSection: [
            'w-full',
            'flex-1',
            'min-h-[400px]',
            'lg:flex-1',
            'lg:h-full',
            'lg:min-h-full',
            'flex',
            'flex-col',
            'text-left'
        ],
        detailsContent: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-24',
            'px-spacing-spacing-40',
            'py-spacing-spacing-40',
            'h-full',
            'justify-start'
        ],
        detailsTitle: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-24'
        ],
        detailsTitleText: [
            'font-typography-font-family-header',
            'font-typography-font-weight-header',
            'text-typography-font-size-header-medium',
            'leading-typography-line-height-medium',
            'text-component-card-item-title'
        ],
        detailsTitleDivider: [
            'w-[100px]',
            'h-[2px]',
            'bg-color-core-color-3-Coral'
        ],
        officeNameText: [
            'font-typography-font-family-body',
            'font-typography-font-weight-body',
            'text-typography-font-size-body-large',
            'leading-typography-line-height-body-large',
            'text-component-card-item-title'
        ],
        addressText: [
            'font-typography-font-family-body',
            'font-typography-font-weight-body',
            'text-typography-font-size-body-medium',
            'leading-typography-line-height-body-medium',
            'text-component-card-item-title',
            '[&_a]:text-color-core-color-4-skyblue-darkTint',
            '[&_a]:underline',
            '[&_a]:decoration-solid'
        ],
        contactInfo: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-4'
        ],
        contactRow: [
            'flex',
            'flex-row',
            'gap-spacing-spacing-4',
            'items-baseline'
        ],
        contactLabel: [
            'font-typography-font-family-body',
            'font-typography-font-weight-body',
            'text-typography-font-size-body-medium',
            'leading-typography-line-height-body-medium',
            'text-component-card-item-title'
        ],
        telephoneLink: [
            'font-typography-font-family-body',
            'font-typography-font-weight-body',
            'text-typography-font-size-body-medium',
            'leading-typography-line-height-body-medium',
            'text-color-core-color-4-skyblue-darkTint',
            'underline',
            'decoration-solid'
        ],
        faxLink: [
            'font-typography-font-family-body',
            'font-typography-font-weight-body',
            'text-typography-font-size-body-medium',
            'leading-typography-line-height-body-medium',
            'text-color-core-color-4-skyblue-darkTint',
            'underline',
            'decoration-solid'
        ],
        openingHoursSection: [
            'flex',
            'flex-col',
            'gap-spacing-spacing-24',
            'mt-spacing-spacing-16'
        ],
        openingHoursTitle: [
            'font-typography-font-family-body',
            'font-typography-font-weight-body',
            'text-typography-font-size-body-large',
            'leading-typography-line-height-body-large',
            'text-component-card-item-title'
        ],
        openingHoursText: [
            'font-typography-font-family-body',
            'font-typography-font-weight-body',
            'text-typography-font-size-body-medium',
            'leading-typography-line-height-body-medium',
            'text-component-card-item-title'
        ],
        ctaContainer: [
            'flex',
            'flex-col',
            'md:flex-row',
            'items-start',
            'md:items-center',
            'gap-spacing-spacing-8',
            'mt-spacing-spacing-24'
        ],
        ctaButtons: [
            '!w-auto'
        ]
    }
});
